import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Container,
  Box,
  Typography,
  CircularProgress,
  Button,
  Paper,
  Divider,
  Chip,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import { doc, getDoc, updateDoc, addDoc, collection, serverTimestamp } from "firebase/firestore";
import { db, auth } from "../firebase";
import Header3 from "./Header3";
import BottomNav from "./BottomNav";

const STATUS_LABELS = {
  pending: { label: "Pendiente", color: "warning" },
  accepted: { label: "Aceptada", color: "success" },
  rejected: { label: "Rechazada", color: "error" },
};

function ProjectProposal() {
  const { projectId, proposalId } = useParams();
  const [loading, setLoading] = useState(true);
  const [proposal, setProposal] = useState(null);
  const [project, setProject] = useState(null);
  const [freelancer, setFreelancer] = useState(null);
  const [updating, setUpdating] = useState(false);
  const navigate = useNavigate();

  const isOwner = project && auth.currentUser?.uid === project.uid;
  const status = proposal?.status || "pending";

  useEffect(() => {
    const fetchProjectAndProposal = async () => {
      try {
        setLoading(true);
        
        // Fetch project details
        const projectDoc = await getDoc(doc(db, "projects", projectId));
        if (projectDoc.exists()) {
          setProject({ id: projectDoc.id, ...projectDoc.data() });
        } else {
          return;
        }

        // Fetch the specific proposal
        const proposalDoc = await getDoc(doc(db, "projects", projectId, "proposals", proposalId));
        if (proposalDoc.exists()) {
          const proposalData = proposalDoc.data();
          setProposal({ id: proposalDoc.id, ...proposalData });

          // Fetch freelancer details
          const freelancerDoc = await getDoc(doc(db, "users", proposalData.freelanceUid));
          if (freelancerDoc.exists()) {
            setFreelancer({ id: freelancerDoc.id, ...freelancerDoc.data() });
          }
        }
      } catch (error) {
        console.error("Error fetching project and proposal:", error);
      } finally {
        setLoading(false);
      }
    };

    if (projectId && proposalId) {
      fetchProjectAndProposal();
    }
  }, [projectId, proposalId]);

  const handleDecision = async (decision) => {
    if (!proposal || !project || !freelancer) return;
    setUpdating(true);
    try {
      await updateDoc(doc(db, "projects", projectId, "proposals", proposalId), { status: decision });
      await updateDoc(doc(db, "proposals", proposalId), { status: decision });

      if (decision === "accepted") {
        await updateDoc(doc(db, "projects", projectId), { status: "en_progreso" });
      }

      await addDoc(collection(db, "notifications"), {
        userId: freelancer.id,
        projectId,
        proposalId,
        message:
          decision === "accepted"
            ? `Tu propuesta para "${project.title}" fue aceptada.`
            : `Tu propuesta para "${project.title}" fue rechazada.`,
        type: decision === "accepted" ? "proposal_accepted" : "proposal_rejected",
        read: false,
        timestamp: serverTimestamp(),
      });

      setProposal((prev) => ({ ...prev, status: decision }));
    } catch (error) {
      console.error("Error updating proposal status:", error);
    } finally {
      setUpdating(false);
    }
  };

  return (
    <Container maxWidth="md">
      <Header3 />
      <Box sx={{ mt: 14, mb: 20 }}>
        {loading ? (
          <Box sx={{ textAlign: "center", color: "primary.main", marginTop: 2.5 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {project && proposal && freelancer ? (
              <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                  <Typography variant="h5" sx={{ color: "primary.main", mb: 2 }}>
                    Propuesta para: {project.title}
                  </Typography>
                  <Chip
                    label={STATUS_LABELS[status].label}
                    color={STATUS_LABELS[status].color}
                  />
                </Box>
                <Divider sx={{ mb: 2 }} />
                <Typography variant="body1" sx={{ mb: 1 }}>
                  <strong>Freelancer:</strong> {freelancer.displayName}
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  <strong>Precio ofertado:</strong> USD${proposal.price}
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  <strong>Presupuesto original:</strong> USD${project.price}
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <strong>Descripción de la propuesta:</strong>
                </Typography>
                <Typography variant="body2" sx={{ mb: 2, whiteSpace: 'pre-wrap' }}>
                  {proposal.description}
                </Typography>
                {isOwner && status === "pending" && (
                  <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
                    <Button
                      variant="contained"
                      color="success"
                      fullWidth
                      startIcon={<CheckCircleIcon />}
                      disabled={updating}
                      onClick={() => handleDecision("accepted")}
                    >
                      Aceptar
                    </Button>
                    <Button
                      variant="outlined"
                      color="error"
                      fullWidth
                      startIcon={<CancelIcon />}
                      disabled={updating}
                      onClick={() => handleDecision("rejected")}
                    >
                      Rechazar
                    </Button>
                  </Box>
                )}
                <Button
                  variant="contained"
                  fullWidth
                  onClick={() => navigate(`/chatbox/${projectId}/${proposalId}/${freelancer.id}/${project.uid}`)}
                  sx={{ mt: 2 }}
                >
                  Chatear con el freelancer
                </Button>
              </Paper>
            ) : (
              <Typography>No se encontró la propuesta o el proyecto.</Typography>
            )}
            <Button
              variant="outlined"
              fullWidth
              onClick={() => navigate(`/home`)}
              sx={{ mt: 2 }}
            >
              Volver a inicio
            </Button>
          </>
        )}
      </Box>
      <BottomNav />
    </Container>
  );
}

export default ProjectProposal;